import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useIngredientLines } from "../../../contexts/IngredientLinesContext";
import { useSidebar } from "../../../contexts/SidebarContext";

export default function IngredientLinesEditor({ readOnly = false, forceBlank = false }) {
  const maxLines = 12;

  const {
    ingredientLines,
    selectedLineId,
    addBlankLine,
    updateLineRawText,
    selectLine,
  } = useIngredientLines();

  const { openUtilities, setActiveUtilitiesTab, utilitiesOpen } = useSidebar();

  const openInspectorFor = useCallback(
    (key) => {
      if (key == null) return;
      selectLine(key);
      setActiveUtilitiesTab("ingredients");
      if (!utilitiesOpen) openUtilities();
    },
    [selectLine, setActiveUtilitiesTab, openUtilities, utilitiesOpen]
  );

  const selectOnly = useCallback(
    (key) => {
      if (key == null) return;
      selectLine(key);
    },
    [selectLine]
  );

  // If user focuses an empty slot, we append a new line and then auto-select it.
  const pendingSelectLastRef = useRef(false);

  // When creating a new blank line from an empty slot, preserve the first typed value.
  const pendingInitialTextRef = useRef("");

  const DEFAULT_CONFIDENCE_THRESHOLD = 0.7;

  // Review should mean "needs action", not "FYI".
  // Only warnings or low confidence trigger review styling.
  const lineNeedsReview = (line, threshold = DEFAULT_CONFIDENCE_THRESHOLD) => {
    if (!line) return false;
    const warningsCount = Array.isArray(line?.warnings) ? line.warnings.length : 0;
    const conf = typeof line?.confidence_score === "number" ? line.confidence_score : null;
    const lowConfidence = conf != null && conf < threshold;
    return warningsCount > 0 || lowConfidence;
  };

  useEffect(() => {
    if (!pendingSelectLastRef.current) return;
    if (!ingredientLines?.length) return;

    const last = ingredientLines[ingredientLines.length - 1];
    const lastKey = last?.id ?? last?.client_key;

    if (lastKey != null) {
      // Preserve the first typed value (if any), but do NOT open the utilities panel.
      if (pendingInitialTextRef.current) {
        updateLineRawText(lastKey, pendingInitialTextRef.current);
        pendingInitialTextRef.current = "";
      }

      // Just select the line quietly; inspector opens only on deliberate row click.
      selectOnly(lastKey);
    }

    pendingSelectLastRef.current = false;
  }, [ingredientLines, selectOnly, updateLineRawText]);

  const rows = useMemo(() => {
    const safe = Array.isArray(ingredientLines) ? ingredientLines : [];
    // pad to 12 for consistent layout
    return Array.from({ length: maxLines }).map((_, i) => safe[i] ?? null);
  }, [ingredientLines]);

  const borderClassForLine = (line) => {
    // Default: match template border exactly
    if (!line) return "border-[#e0c3a5]";

    const status = line?.parse_status;

    // Error: strongest attention
    if (status === "error") return "border-red-400";

    // Pending: soft attention
    if (status === "pending") return "border-slate-300";

    // Needs review: amber attention (warnings/low confidence)
    if (lineNeedsReview(line)) return "border-amber-400";

    // OK: template border
    return "border-[#e0c3a5]";
  };

  return (
    <div className="w-full h-full flex flex-col text-[#5a3e36] pt-0 pb-0">
      <label className="text-[clamp(0.65rem,1vw,0.875rem)] font-semibold text-center mb-[1%] leading-tight">
        Ingredients:
      </label>

      <div className="flex flex-col justify-between h-full w-full overflow-hidden p-0 m-0 gap-[1%]">
        {rows.map((line, i) => {
          // Blank-template previews share the ingredient-lines context with the
          // live editor. Do not let the saved recipe's lines leak into exports.
          const displayLine = forceBlank ? null : line;
          const lineKey = displayLine?.id ?? displayLine?.client_key ?? null;
          const isSelected = lineKey != null && selectedLineId === lineKey;
          const raw = (displayLine?.raw_text ?? "").toString();

          return (
            <div
              key={lineKey ?? `empty_${i}`}
              onClick={() => {
                if (lineKey != null) openInspectorFor(lineKey);
              }}
              className={[
                "w-full min-w-0 h-[clamp(7.5%,8.5%,9%)] min-h-[24px] rounded border bg-white overflow-hidden",
                borderClassForLine(displayLine),
                isSelected ? "ring-1 ring-[#e0c3a5]" : "",
              ].join(" ")}
            >
              {/* Raw text (same tight header area) */}
              <div className="h-full px-[2%] flex items-center text-[clamp(0.65rem,0.95vw,0.875rem)] font-sans text-[#5a3e36] leading-tight">
                {readOnly ? (
                  <span className="truncate w-full">
                  {raw.trim() || (
                      <span className="text-transparent select-none">.</span>
                    )}
                  </span>
                ) : (
                  <input
                    value={raw}
                    placeholder="Ingredient line"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={() => {
                      // if user clicks into an empty slot, create a new blank line
                      if (!displayLine) {
                        pendingSelectLastRef.current = true;
                        addBlankLine();
                        return;
                      }
                      if (lineKey != null) selectOnly(lineKey);
                    }}
                    onChange={(e) => {
                      // if the row doesn't exist yet, create then let effect select it
                      if (!displayLine) {
                        pendingInitialTextRef.current = e.target.value;
                        pendingSelectLastRef.current = true;
                        addBlankLine();
                        return;
                      }
                      if (lineKey == null) return;
                      updateLineRawText(lineKey, e.target.value);
                    }}
                    className="w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0 p-0 m-0"
                    style={{ fontSize: 8, lineHeight: 1 }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
